import { Checkbox } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import Modal from "react-responsive-modal";
import { useHistory } from "react-router";
import { useAuth } from "../../hooks/auth";
import { useConsultant } from "../../hooks/consultant";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import {
  Content,
  TitleContainer,
  ConsultantsAndOptionContainer,
  ConsultantsContainer,
  ConsultantContent,
  OptionContainer,
  ButtonsContainer
} from "./styles";

interface IModalPropertiesPerConsultant {
  open: boolean;
  functionToCloseModal(): void;
}

interface IConsultants {
  id: number;
  first_name: string;
}

export default function ModalPropertiesPerConsultant({ open, functionToCloseModal }: IModalPropertiesPerConsultant) {
  const { dateToExpires, access_token, user } = useAuth();
  const { addToast } = useToast();
  const { consultantsSaved, handleAddConsultants } = useConsultant();
  const history = useHistory();

  const [users, setUsers] = useState<IConsultants[]>([]);
  const [selectedConsultants, setSelectedConsultants] = useState<number[]>([]);
  const [onlyActives, setOnlyActives] = useState(false);
  const [loadingShowScreenRequest, setLoadingShowScreenRequest] = useState(false);
  const [loadingExcelRequest, setLoadingExcelRequest] = useState(false);

  useEffect(() => {
    async function loadApi() {
      try {
        if (user?.role === 'admin') {
          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authotization = `Bearer ${token || access_token}`;

          if (consultantsSaved.length < 1) {
            const response = await api.get('/api/adm/users', {
              params: {
                role: 'consultant'
              }
            });
            handleAddConsultants(response.data.data.consultants);
            setUsers(response.data.data.consultants);
          } else {
            setUsers(consultantsSaved);
          }
        }
      } catch (err:any) {
        if (err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: 'error'
          });
        }
      }
    }

    if (user) {
      loadApi();
    } else {
      history.push('/');
    }
  }, [consultantsSaved, access_token, dateToExpires, user, history, handleAddConsultants, addToast]);

  const handleConsultantSelection = (consultantId: number) => {
    setSelectedConsultants(prev =>
      prev.includes(consultantId)
        ? prev.filter(id => id !== consultantId)
        : [...prev, consultantId]
    );
  };

  const handleShowReport = useCallback(async () => {
    setLoadingShowScreenRequest(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;

      const response = await api.post('/api/adm/reports/propertyByConsultant', {
        consultant_ids: selectedConsultants.join(','),
        only_active: onlyActives ? 1 : 0,
        type: 'screen'
      });

      if (response.data.success) {
        history.push({
          pathname: '/show-properties-per-consultant-report',
          state: {
            data: response.data.data,
            consultant_names: users
              .filter(u => selectedConsultants.includes(u.id))
              .map(u => u.first_name)
              .join(', ')
          }
        });
        functionToCloseModal();
      }
    } catch (err:any) {
      addToast({
        title: 'Erro ao gerar relatório',
        type: 'error'
      });
    }
    setLoadingShowScreenRequest(false);
  }, [dateToExpires, access_token, selectedConsultants, onlyActives, history, functionToCloseModal, users, addToast]);

  const handleExcelDownloadReport = useCallback(async () => {
    setLoadingExcelRequest(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;

      if (selectedConsultants.length === 0) {
        addToast({
          title: 'Selecione pelo menos um consultor',
          type: 'error'
        });
        return;
      }

      const response = await api.post(
        '/api/adm/reports/propertyByConsultant',
        {
          consultant_ids: selectedConsultants.join(','),
          only_active: onlyActives ? 1 : 0,
          type: 'excel'
        },
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Propriedades_consultores_${selectedConsultants.join('_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err:any) {
      addToast({
        title: 'Erro ao baixar relatório',
        type: 'error'
      });
    } finally {
      setLoadingExcelRequest(false);
    }
  }, [dateToExpires, access_token, selectedConsultants, onlyActives, addToast]);

  const isVisualizeDisabled = loadingShowScreenRequest || selectedConsultants.length !== 1;

  return (
    <Modal open={open} onClose={functionToCloseModal} classNames={{
      modal: 'modalPropertiesPerConsultant'
    }} closeIcon={<FiX size={18} color="#000" />}>
      <Content>
        <TitleContainer>
          <h3>IMÓVEIS POR CONSULTOR</h3>
        </TitleContainer>

        <ConsultantsAndOptionContainer>
          <ConsultantsContainer>
            {users.map(user => (
              <ConsultantContent key={user.id}>
                <Checkbox
                  checked={selectedConsultants.includes(user.id)}
                  onChange={() => handleConsultantSelection(user.id)}
                />
                <p onClick={() => handleConsultantSelection(user.id)}>{user.first_name}</p>
              </ConsultantContent>
            ))}
          </ConsultantsContainer>

          <OptionContainer>
            <Checkbox checked={onlyActives} onChange={() => setOnlyActives(!onlyActives)} />
            <p onClick={() => setOnlyActives(!onlyActives)}>Apenas imóveis ativos?</p>
          </OptionContainer>
        </ConsultantsAndOptionContainer>

        <ButtonsContainer>
          <button
            onClick={loadingExcelRequest ? undefined : handleExcelDownloadReport}
            disabled={loadingExcelRequest || selectedConsultants.length === 0}
          >
            {loadingExcelRequest ? 'Carregando...' : "Exportar Excel"}
          </button>
          <button
            onClick={isVisualizeDisabled ? undefined : handleShowReport}
            disabled={isVisualizeDisabled}
            style={{
              opacity: isVisualizeDisabled ? 0.6 : 1,
              cursor: isVisualizeDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            {loadingShowScreenRequest ? 'Carregando...' : "Visualizar Relatório"}
            {selectedConsultants.length > 1 && (
              <span style={{ display: 'block', fontSize: '12px', color: '#ff0000' }}>
                (Selecione apenas 1 consultor para visualizar)
              </span>
            )}
          </button>
        </ButtonsContainer>
      </Content>
    </Modal>
  );
}